import { useState, useEffect, useCallback, createContext, useContext } from 'react';

/**
 * ResponsiveOverrideContext
 *
 * viewport 대신 임의의 폭을 useResponsive 의 기준으로 사용하고 싶을 때 사용한다.
 * (예: 분할 뷰의 오른쪽 패널 안에서 패널 폭 기준으로 모바일 UI를 적용)
 *
 * 사용: <ResponsiveOverrideContext.Provider value={panelWidth}>...</ResponsiveOverrideContext.Provider>
 * null 이면 기존 viewport 기반 동작 유지.
 */
export const ResponsiveOverrideContext = createContext<number | null>(null);

/**
 * 반응형 브레이크포인트 상수 (단일 소스 of truth)
 *
 * ┌─────────────────────────────────────────────────────────────────┐
 * │ 의미         │ 값      │ JS Hook            │ Tailwind (CSS)  │
 * ├─────────────────────────────────────────────────────────────────┤
 * │ SmallMobile  │ <480px  │ isSmallMobile       │ xs: (≥480)      │
 * │ Mobile       │ <768px  │ isMobile            │ md: (≥768)      │
 * │ Sidebar      │ <1024px │ isSidebarCollapsed  │ lg: (≥1024)     │
 * │ Desktop      │ ≥1200px │ isDesktop           │ desktop: (≥1200)│
 * └─────────────────────────────────────────────────────────────────┘
 *
 * 사용 규칙 3원칙:
 * 1. 스타일만 바뀌면 → Tailwind breakpoint (md:px-4, desktop:flex)
 * 2. 컴포넌트가 교체되면 → JS hook (isSmallMobile ? <A/> : <B/>)
 * 3. 같은 요소에 CSS와 JS 혼합 금지
 */
export const BREAKPOINTS = {
    /** 모바일/태블릿과 데스크톱을 구분하는 기준 — Tailwind: desktop: */
    DESKTOP: 1200,
    /** 사이드바 표시/숨김 기준 — Tailwind: lg: */
    SIDEBAR_COLLAPSE: 1024,
    /** 태블릿과 모바일을 구분하는 기준 — Tailwind: md: */
    TABLET: 768,
    /** 모바일 기준 — Tailwind: xs: */
    MOBILE: 480,
} as const;

interface WindowSize {
    innerWidth: number;
    innerHeight: number;
}

interface ResponsiveState {
    /** 현재 윈도우 크기 */
    windowSize: WindowSize;
    /** 데스크톱 화면인지 (>= 1200px) */
    isDesktop: boolean;
    /** 모바일/태블릿 화면인지 (< 1200px) */
    isMobileOrTablet: boolean;
    /** 사이드바가 접혀야 하는지 (< 1024px) */
    isSidebarCollapsed: boolean;
    /** 태블릿 화면인지 (768px ~ 1199px) */
    isTablet: boolean;
    /** 모바일 화면인지 (< 768px) */
    isMobile: boolean;
    /** 작은 모바일 화면인지 (< 480px) */
    isSmallMobile: boolean;
}

/**
 * 화면 크기에 따른 반응형 상태를 제공하는 커스텀 훅
 * RoomHeader와 RoomRightMenu에서 동일하게 사용
 */
export function useResponsive(): ResponsiveState {
    const getWindowSize = useCallback((): WindowSize => {
        return {
            innerWidth: window.innerWidth,
            innerHeight: window.innerHeight,
        };
    }, []);

    const [windowSize, setWindowSize] = useState<WindowSize>(getWindowSize);
    const overrideWidth = useContext(ResponsiveOverrideContext);

    useEffect(() => {
        function handleResize() {
            setWindowSize(getWindowSize());
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [getWindowSize]);

    // override가 주입되면 그 폭을 기준으로 모든 breakpoint 재계산 (컨테이너 기반 반응형)
    const effectiveWidth = overrideWidth ?? windowSize.innerWidth;

    const isDesktop = effectiveWidth >= BREAKPOINTS.DESKTOP;
    const isMobileOrTablet = effectiveWidth < BREAKPOINTS.DESKTOP;
    const isSidebarCollapsed = effectiveWidth < BREAKPOINTS.SIDEBAR_COLLAPSE;
    const isTablet = effectiveWidth >= BREAKPOINTS.TABLET && effectiveWidth < BREAKPOINTS.DESKTOP;
    const isMobile = effectiveWidth < BREAKPOINTS.TABLET;
    const isSmallMobile = effectiveWidth < BREAKPOINTS.MOBILE;

    return {
        // windowSize 자체는 실제 viewport를 그대로 유지 (override는 폭 분기만)
        windowSize,
        isDesktop,
        isMobileOrTablet,
        isSidebarCollapsed,
        isTablet,
        isMobile,
        isSmallMobile,
    };
}

/**
 * 오른쪽 메뉴 표시/숨기기 유틸리티 함수
 */
export function showRightMenu() {
    document.getElementById('right-menu')?.classList.add('show-right-menu');
}

export function hideRightMenu() {
    document.getElementById('right-menu')?.classList.remove('show-right-menu');
}
